import classNames from "classnames"
import { useLayoutEffect, useRef, useState } from "react"

import VideoBlock from "@/pages/TrackingPage/ThemeLayoutCustomize/components/VideoBlock/VideoBlock"

import ImageGrid from "../ImageGrid/ImageGrid"
import TrackingBlock from "../TrackingBlock/TrackingBlock"
import styles from "./image.module.scss"
import { useShowcaseThemeLayout } from "./ThemeLayoutLeftTabPanels/ShowcaseThemeLayoutContext"
import { mapBottomImagesToImageGridItems } from "./ThemeLayoutLeftTabPanels/themeLayoutBackdropEditor"
import {
  getShowcaseHeroLayoutFromOrder,
  SHOWCASE_TOP_ORDER_LOOKUP_ROW_ID,
} from "./ThemeLayoutLeftTabPanels/themeLayoutShowcaseDraft"
import type { ThemeLayoutPreviewViewport } from "./themeLayoutPreviewViewport"

const containerClassname = "mx-auto flex w-full max-w-[1176px] px-4"

const colSpanClass = (n: number): string => {
  const map: Record<number, string> = {
    1: "col-span-1",
    2: "col-span-2",
    3: "col-span-3",
    4: "col-span-4",
    5: "col-span-5",
    6: "col-span-6",
    7: "col-span-7",
    8: "col-span-8",
    9: "col-span-9",
    10: "col-span-10",
    11: "col-span-11",
    12: "col-span-12",
  }

  return map[n] ?? "col-span-12"
}

const cardRadiusClass = (displayCornerRadius: "ROUNDED" | "SHARP" | "STANDARD"): string => {
  if (displayCornerRadius === "SHARP") {
    return "rounded-none"
  }
  if (displayCornerRadius === "ROUNDED") {
    return "rounded-2xl"
  }
  return "rounded-lg"
}

export interface ThemeLayoutPreviewShowcaseProps {
  viewport: ThemeLayoutPreviewViewport
}

/**
 * Showcase 预览：首行列宽与顺序来自左侧草稿（column order + Width 下拉），其余与 Backdrop 一致。
 */
const ThemeLayoutPreviewShowcase = ({ viewport }: ThemeLayoutPreviewShowcaseProps) => {
  const { draft, bottomSectionPopupDraft, topSectionImagesPopupDraft } = useShowcaseThemeLayout()
  const isMobile = viewport === "mobile"
  // 弹窗编辑期间使用 overlay 数据做实时预览，其余时间使用 store 数据
  const effectiveTopImages = topSectionImagesPopupDraft ?? draft.top_section_images
  const { orderedEnabledIds, colSpanById } = getShowcaseHeroLayoutFromOrder({
    columnOrder: draft.top_section_column_order,
    images: effectiveTopImages,
    orderLookupWidthPercent: draft.width,
  })
  const imageById = new Map(effectiveTopImages.map((item) => [item.id, item]))
  const r = cardRadiusClass(draft.corner_radius)

  // 图片 cell 数（排除 query 卡片）
  const imageColumnCount = orderedEnabledIds.filter((id) => id !== SHOWCASE_TOP_ORDER_LOOKUP_ROW_ID).length

  // 基准高度：以"2 图布局中每图占 4 列"的高度为准（height = 4列宽 × 5/3）。
  // 当列数不足（仅 widget / 单图）时套用它，使高度与 2/3 列布局一致，而非各自的最小/比例高度。
  // 公式：col_unit = (W - 11×gap) / 12；ref_width = 4×col_unit + 3×gap；height = ref_width × 5/3，gap = 20px。
  // 从网格容器宽反推 col_unit（与按单元格测量等价）；useLayoutEffect 在首帧前完成计算，避免闪烁。
  const needsReferenceHeight = !isMobile && imageColumnCount <= 1
  const heroGridRef = useRef<HTMLDivElement>(null)
  const [referenceHeroHeight, setReferenceHeroHeight] = useState<number | null>(null)

  useLayoutEffect(() => {
    const el = heroGridRef.current

    if (!el || !needsReferenceHeight) {
      setReferenceHeroHeight(null)

      return
    }

    const gap = 20
    const cols = 12
    const refColSpan = 4
    const compute = (width: number) => {
      const colUnit = (width - (cols - 1) * gap) / cols
      const refWidth = refColSpan * colUnit + (refColSpan - 1) * gap

      setReferenceHeroHeight(refWidth * 5 / 3)
    }

    compute(el.getBoundingClientRect().width)

    const observer = new ResizeObserver((entries) => compute(entries[0].contentRect.width))

    observer.observe(el)

    return () => observer.disconnect()
  }, [needsReferenceHeight])

  // popup 打开时使用 overlay 数据做实时预览，关闭后回退到 store 数据
  const effectiveBottomSection = bottomSectionPopupDraft ?? draft.bottom_section
  const bottomVideo = effectiveBottomSection.video
  const videoRaw = bottomVideo?.youtubeUrl?.trim()
  const shouldShowVideo = bottomVideo !== undefined && bottomVideo.enabled !== false
  const bottomGridItems = mapBottomImagesToImageGridItems(effectiveBottomSection.images)

  const renderHeroImageCell = (imageId: string, colSpan: number) => {
    const item = imageById.get(imageId)
    const imageUrl = item?.image?.url?.trim() ?? ""
    const imageAlt = item?.image?.alt?.trim() ?? ""
    const hasImageWithoutUrl = Boolean(item?.image?.shopifyFileId?.trim()) && !imageUrl

    // 移动端：有图则自适应高度（原生比例），无图则默认占位高度
    if (isMobile) {
      if (imageUrl) {
        return (
          <div
            key={imageId}
            className={classNames(
              "overflow-hidden border border-solid border-[#EBEBEB]",
              r,
              styles.imageBlock,
            )}
          >
            <img
              alt={imageAlt}
              className="block w-full h-auto"
              decoding="async"
              loading="lazy"
              src={imageUrl}
            />
          </div>
        )
      }

      return (
        <div
          key={imageId}
          className={classNames(
            "relative overflow-hidden border border-solid border-[#EBEBEB] h-[160px]",
            r,
            styles.imageBlock,
          )}
        >
          <div
            aria-hidden={hasImageWithoutUrl ? undefined : true}
            className="absolute inset-0 block w-full bg-[#F1F1F1]"
          />
        </div>
      )
    }

    // 桌面端单图：固定高度（等同 2 图时的高度）；2 图：aspect-[3/5]
    const useFixedHeight = imageColumnCount === 1 && referenceHeroHeight !== null

    return (
      <div
        key={imageId}
        style={useFixedHeight ? { height: referenceHeroHeight } : undefined}
        className={classNames(
          "relative overflow-hidden border border-solid border-[#EBEBEB]",
          useFixedHeight ? "" : "aspect-[3/5]",
          r,
          styles.imageBlock,
          colSpanClass(colSpan),
        )}
      >
        {imageUrl ? (
          <img
            alt={imageAlt}
            className="absolute inset-0 block h-full w-full object-cover"
            decoding="async"
            loading="lazy"
            src={imageUrl}
          />
        ) : (
          <div
            aria-hidden={hasImageWithoutUrl ? undefined : true}
            className="absolute inset-0 block w-full bg-[#F1F1F1]"
          />
        )}
      </div>
    )
  }

  const renderHeroColumn = (columnId: string) => {
    const colSpan = colSpanById.get(columnId) ?? 12

    if (columnId === SHOWCASE_TOP_ORDER_LOOKUP_ROW_ID) {
      // widget 与图片 cell 等高：仅 widget（0 图）或 widget+单图（1 图）时都用基准高度，
      // 与图片 cell 的 useFixedHeight 口径一致；仅 2 图及以上才走 aspect-[3/5]。
      // 否则 2 列各占 50% 时 widget 按自身宽算 aspect 会比定高的图片更高。
      const useFixedHeight = !isMobile && imageColumnCount <= 1 && referenceHeroHeight !== null
      const queryCardClass = classNames(
        "min-h-0 bg-white",
        isMobile
          ? "h-auto pt-[30px]"
          : classNames(
            "border border-solid border-[#e6e6e6] py-[30px] px-[20px] overflow-auto",
            imageColumnCount > 1 && "aspect-[3/5]",
          ),
        r,
        !isMobile && colSpanClass(colSpan),
      )

      return (
        <div
          key={columnId}
          className={queryCardClass}
          style={useFixedHeight ? { height: referenceHeroHeight } : undefined}
        >
          <TrackingBlock hideCardBorder={isMobile} />
        </div>
      )
    }

    return renderHeroImageCell(columnId, colSpan)
  }

  return (
    <div
      className={classNames(
        "flex w-full min-w-0 flex-1 flex-col gap-[40px] pb-6",
      )}
    >
      <div className={classNames("pt-3 md:pt-4")}>
        <div className={classNames(containerClassname)}>
          {isMobile ? (
            <div className="flex w-full min-w-0 flex-col gap-5">
              {orderedEnabledIds.map((columnId) => renderHeroColumn(columnId))}
            </div>
          ) : (
            <div ref={heroGridRef} className="grid w-full min-h-0 grid-cols-12 gap-5">
              {orderedEnabledIds.map((columnId) => renderHeroColumn(columnId))}
            </div>
          )}
        </div>
      </div>

      {
        bottomGridItems.length > 0 && (
          <div className={classNames(containerClassname)}>
            <ImageGrid
              cornerRadiusClass={r}
              items={bottomGridItems}
              ratioMode={effectiveBottomSection.imageRatio}
              viewport={isMobile ? "mobile" : "desktop"}
            />
          </div>
        )
      }

      {shouldShowVideo ? (
        <div className={classNames(containerClassname)}>
          <VideoBlock cornerRadiusClass={r} youtubeUrl={videoRaw || ""} />
        </div>
      ) : null}
    </div>
  )
}

export default ThemeLayoutPreviewShowcase
